import {
  attachDataValues,
  getMentionCharIndex,
  hasValidChars,
  hasValidMentionCharIndex,
} from './utils';
import './quill.customEmoji.css';
import './blots';

const Keys = {
  TAB: 9,
  ENTER: 13,
  ESCAPE: 27,
  UP: 38,
  DOWN: 40,
};

class CustomEmoji {
  isOpen: boolean;
  itemIndex: number;
  emojiCharPos: any;
  cursorPos: any;
  values: any[];
  suspendMouseEnter: boolean;
  existingSourceExecutionToken: any;
  quill: any;
  options: any;
  emojiContainer: any;
  emojiList: any;

  constructor(quill, options) {
    this.isOpen = false;
    this.itemIndex = 0;
    this.emojiCharPos = null;
    this.cursorPos = null;
    this.values = [];
    this.suspendMouseEnter = false;
    //this token is an object that may contains one key "abandoned", set to
    //true when the previous source call should be ignored in favor or a
    //more recent execution.  This token will be null unless a source call
    //is in progress.
    this.existingSourceExecutionToken = null;

    this.quill = quill;

    this.options = {
      source: null,
      renderItem(item) {
        return `${item.value}`;
      },
      renderLoading() {
        return null;
      },
      onSelect(item, insertItem) {
        insertItem(item);
      },
      mentionDenotationChars: ['/'],
      showDenotationChar: true,
      allowedChars: /^[ㄱ-ㅎ|가-힣|a-z|A-Z|0-9|]+$/,
      minChars: 0,
      maxChars: 31,
      offsetTop: 2,
      offsetLeft: 0,
      isolateCharacter: false,
      fixMentionsToQuill: false,
      positioningStrategy: 'normal',
      defaultMenuOrientation: 'bottom',
      blotName: 'mention',
      dataAttributes: [
        'id',
        'value',
        'photoUrl',
        'denotationChar',
        'link',
        'target',
        'disabled',
      ],
      linkTarget: '_blank',
      onOpen() {
        return true;
      },
      onBeforeClose() {
        return true;
      },
      onClose() {
        return true;
      },
      // Style options
      listItemClass: 'ql-mention-list-item',
      emojiContainerClass: 'ql-mention-list-container',
      emojiListClass: 'ql-mention-list',
      selectKeys: [Keys.ENTER],
    };

    Object.assign(this.options, options, {
      dataAttributes: Array.isArray(options.dataAttributes)
        ? this.options.dataAttributes.concat(options.dataAttributes)
        : this.options.dataAttributes,
    });

    //create mention container
    this.emojiContainer = document.createElement('div');
    this.emojiContainer.className = this.options.emojiContainerClass
      ? this.options.emojiContainerClass
      : '';
    this.emojiContainer.style.cssText = 'display: none; position: absolute;';
    this.emojiContainer.onmousemove = this.onContainerMouseMove.bind(this);

    if (this.options.fixMentionsToQuill) {
      this.emojiContainer.style.width = 'auto';
    }

    this.emojiList = document.createElement('ul');
    this.emojiList.id = 'quill-mention-list';
    quill.root.setAttribute('aria-owns', 'quill-mention-list');
    this.emojiList.className = this.options.emojiListClass
      ? this.options.emojiListClass
      : '';
    this.emojiContainer.appendChild(this.emojiList);

    quill.on('text-change', this.onTextChange.bind(this));
    quill.on('selection-change', this.onSelectionChange.bind(this));

    //Pasting doesn't fire selection-change after the pasted text is
    //inserted, so here we manually trigger one
    quill.container.addEventListener('paste', () => {
      setTimeout(() => {
        const range = quill.getSelection();
        this.onSelectionChange(range);
      });
    });

    quill.keyboard.addBinding(
      {
        key: Keys.TAB,
      },
      this.selectHandler.bind(this),
    );
    quill.keyboard.bindings[Keys.TAB].unshift(
      quill.keyboard.bindings[Keys.TAB].pop(),
    );

    for (let selectKey of this.options.selectKeys) {
      quill.keyboard.addBinding(
        {
          key: selectKey,
        },
        this.selectHandler.bind(this),
      );
    }
    quill.keyboard.bindings[Keys.ENTER].unshift(
      quill.keyboard.bindings[Keys.ENTER].pop(),
    );

    quill.keyboard.addBinding(
      {
        key: Keys.ESCAPE,
      },
      this.escapeHandler.bind(this),
    );

    quill.keyboard.addBinding(
      {
        key: Keys.UP,
      },
      this.upHandler.bind(this),
    );

    quill.keyboard.addBinding(
      {
        key: Keys.DOWN,
      },
      this.downHandler.bind(this),
    );
  }

  selectHandler() {
    if (this.isOpen && !this.existingSourceExecutionToken) {
      this.selectItem();
      return false;
    }
    return true;
  }

  escapeHandler() {
    if (this.isOpen) {
      if (this.existingSourceExecutionToken) {
        this.existingSourceExecutionToken.abandoned = true;
      }
      this.hideEmojiList();
      return false;
    }
    return true;
  }

  upHandler() {
    if (this.isOpen && !this.existingSourceExecutionToken) {
      this.prevItem();
      return false;
    }
    return true;
  }

  downHandler() {
    if (this.isOpen && !this.existingSourceExecutionToken) {
      this.nextItem();
      return false;
    }
    return true;
  }

  showEmojiList() {
    if (this.options.positioningStrategy === 'fixed') {
      document.body.appendChild(this.emojiContainer);
    } else {
      this.quill.container.appendChild(this.emojiContainer);
    }

    this.emojiContainer.style.visibility = 'hidden';
    this.emojiContainer.style.display = '';
    this.emojiContainer.scrollTop = 0;
    this.setEmojiContainerPosition();
    this.setIsOpen(true);
  }

  hideEmojiList() {
    this.options.onBeforeClose();
    this.emojiContainer.style.display = 'none';
    this.emojiContainer.remove();
    this.setIsOpen(false);
    this.quill.root.removeAttribute('aria-activedescendant');
  }

  highlightItem(scrollItemInView = true) {
    for (let i = 0; i < this.emojiList.childNodes.length; i += 1) {
      this.emojiList.childNodes[i].classList.remove('selected');
    }

    if (
      this.itemIndex === -1 ||
      this.emojiList.childNodes[this.itemIndex].dataset.disabled === 'true'
    ) {
      return;
    }

    this.emojiList.childNodes[this.itemIndex].classList.add('selected');
    this.quill.root.setAttribute(
      'aria-activedescendant',
      this.emojiList.childNodes[this.itemIndex].id,
    );

    if (scrollItemInView) {
      const itemHeight = this.emojiList.childNodes[this.itemIndex].offsetHeight;
      const itemPos = this.emojiList.childNodes[this.itemIndex].offsetTop;
      const containerTop = this.emojiContainer.scrollTop;
      const containerBottom = containerTop + this.emojiContainer.offsetHeight;

      if (itemPos < containerTop) {
        // Scroll up if the item is above the top of the container
        this.emojiContainer.scrollTop = itemPos;
      } else if (itemPos > containerBottom - itemHeight) {
        // scroll down if any part of the element is below the bottom of the container
        this.emojiContainer.scrollTop += itemPos - containerBottom + itemHeight;
      }
    }
  }

  getItemData() {
    const { link } = this.emojiList.childNodes[this.itemIndex].dataset;
    const hasLinkValue = typeof link !== 'undefined';
    const itemTarget = this.emojiList.childNodes[this.itemIndex].dataset.target;
    if (hasLinkValue) {
      this.emojiList.childNodes[
        this.itemIndex
      ].dataset.value = `<a href="${link}" target=${
        itemTarget || this.options.linkTarget
      }>${this.emojiList.childNodes[this.itemIndex].dataset.value}`;
    }
    return this.emojiList.childNodes[this.itemIndex].dataset;
  }

  onContainerMouseMove() {
    this.suspendMouseEnter = false;
  }

  insertItem(data: any, programmaticInsert?: any) {
    const render = data;

    if (render === null) {
      return;
    }
    if (!this.options.showDenotationChar) {
      render.denotationChar = '';
    }

    let insertAtPos;

    if (!programmaticInsert) {
      insertAtPos = this.emojiCharPos;
      this.quill.deleteText(
        this.emojiCharPos,
        this.cursorPos - this.emojiCharPos,
      );
    } else {
      insertAtPos = this.cursorPos;
    }

    this.quill.insertEmbed(insertAtPos, this.options.blotName, render);
    this.quill.insertEmbed(insertAtPos + 1, 'text', ' '); // 글자 뒤 공백
    // setSelection here sets cursor position
    this.quill.setSelection(insertAtPos + 2);
    this.hideEmojiList();
  }

  selectItem() {
    if (this.itemIndex === -1) {
      return;
    }
    const data = this.getItemData();

    if (data.disabled) {
      return;
    }
    this.options.onSelect(data, (asyncData) => {
      this.insertItem(asyncData);
    });
    this.hideEmojiList();
  }

  onItemMouseEnter(e) {
    if (this.suspendMouseEnter) {
      return;
    }

    const index = Number(e.target.dataset.index);

    if (!Number.isNaN(index) && index !== this.itemIndex) {
      this.itemIndex = index;
      this.highlightItem(false);
    }
  }

  onDisabledItemMouseEnter(e) {
    if (this.suspendMouseEnter) {
      return;
    }

    this.itemIndex = -1;
    this.highlightItem(false);
  }

  onItemClick(e) {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    this.itemIndex = e.currentTarget.dataset.index;
    this.highlightItem();
    this.selectItem();
  }

  onItemMouseDown(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  renderLoading() {
    var renderedLoading = this.options.renderLoading();
    if (!renderedLoading) {
      return;
    }

    if (
      this.emojiContainer.getElementsByClassName('ql-mention-loading').length >
      0
    ) {
      this.showEmojiList();
      return;
    }

    this.emojiList.innerHTML = '';
    var loadingDiv = document.createElement('div');
    loadingDiv.className = 'ql-mention-loading';
    loadingDiv.innerHTML = this.options.renderLoading();
    this.emojiContainer.append(loadingDiv);
    this.showEmojiList();
  }

  removeLoading() {
    var loadingDiv =
      this.emojiContainer.getElementsByClassName('ql-mention-loading');
    if (loadingDiv.length > 0) {
      loadingDiv[0].remove();
    }
  }

  renderList(mentionChar, data, searchTerm) {
    if (data && data.length > 0) {
      this.removeLoading();

      this.values = data;
      this.emojiList.innerHTML = '';

      var initialSelection = -1;

      for (let i = 0; i < data.length; i += 1) {
        const li = document.createElement('li');

        li.id = 'quill-emoji-item-' + i;
        li.className = this.options.listItemClass
          ? this.options.listItemClass
          : '';
        if (data[i].disabled) {
          li.className += ' disabled';
          li.setAttribute('aria-hidden', 'true');
        } else if (initialSelection === -1) {
          initialSelection = i;
        }
        li.dataset.index = i.toString();

        // thumbnail
        const thumbSec = document.createElement('div');
        thumbSec.className = 'ql-emoji-thumbnail';
        thumbSec.style.backgroundImage = `url(${data[i].photoURL})`;
        li.appendChild(thumbSec);

        // value
        const valueSec = document.createElement('span');
        li.appendChild(valueSec);
        valueSec.innerHTML = this.options.renderItem(data[i], searchTerm);
        if (!data[i].disabled) {
          li.onmouseenter = this.onItemMouseEnter.bind(this);
          li.onmouseup = this.onItemClick.bind(this);
          li.onmousedown = this.onItemMouseDown.bind(this);
        } else {
          li.onmouseenter = this.onDisabledItemMouseEnter.bind(this);
        }
        li.dataset.denotationChar = mentionChar;
        li.dataset.photoUrl = data[i].photoURL;

        this.emojiList.appendChild(
          attachDataValues(li, data[i], this.options.dataAttributes),
        );
      }
      this.itemIndex = initialSelection;
      this.highlightItem();
      this.showEmojiList();
    } else {
      this.hideEmojiList();
    }
  }

  nextItem() {
    var increment = 0;
    var newIndex;

    do {
      increment++;
      newIndex = (this.itemIndex + increment) % this.values.length;
      var disabled =
        this.emojiList.childNodes[newIndex].dataset.disabled === 'true';
      if (increment === this.values.length + 1) {
        //we've wrapped around w/o finding an enabled item
        newIndex = -1;
        break;
      }
    } while (disabled);

    this.itemIndex = newIndex;
    this.suspendMouseEnter = true;
    this.highlightItem();
  }

  prevItem() {
    var decrement = 0;
    var newIndex;

    do {
      decrement++;
      newIndex =
        (this.itemIndex + this.values.length - decrement) % this.values.length;
      var disabled =
        this.emojiList.childNodes[newIndex].dataset.disabled === 'true';
      if (decrement === this.values.length + 1) {
        //we've wrapped around w/o finding an enabled item
        newIndex = -1;
        break;
      }
    } while (disabled);

    this.itemIndex = newIndex;
    this.suspendMouseEnter = true;
    this.highlightItem();
  }

  containerBottomIsNotVisible(topPos, containerPos) {
    const emojiContainerBottom =
      topPos + this.emojiContainer.offsetHeight + containerPos.top;
    return emojiContainerBottom > window.pageYOffset + window.innerHeight;
  }

  containerRightIsNotVisible(leftPos, containerPos) {
    if (this.options.fixMentionsToQuill) {
      return false;
    }

    const rightPos =
      leftPos + this.emojiContainer.offsetWidth + containerPos.left;
    const browserWidth =
      window.pageXOffset + document.documentElement.clientWidth;
    return rightPos > browserWidth;
  }

  setIsOpen(isOpen) {
    if (this.isOpen !== isOpen) {
      if (isOpen) {
        this.options.onOpen();
      } else {
        this.options.onClose();
      }
      this.isOpen = isOpen;
    }
  }

  setEmojiContainerPosition() {
    if (this.options.positioningStrategy === 'fixed') {
      this.setEmojiContainerPosition_Fixed();
    } else {
      this.setEmojiContainerPosition_Normal();
    }
  }

  setEmojiContainerPosition_Normal() {
    const containerPos = this.quill.container.getBoundingClientRect();
    const emojiCharPos = this.quill.getBounds(this.emojiCharPos);
    const containerHeight = this.emojiContainer.offsetHeight;

    let topPos = this.options.offsetTop;
    let leftPos = this.options.offsetLeft;

    // handle horizontal positioning
    if (this.options.fixMentionsToQuill) {
      const rightPos = 0;
      this.emojiContainer.style.right = `${rightPos}px`;
    } else {
      leftPos += emojiCharPos.left;
    }

    if (this.containerRightIsNotVisible(leftPos, containerPos)) {
      const containerWidth =
        this.emojiContainer.offsetWidth + this.options.offsetLeft;
      const quillWidth = containerPos.width;
      leftPos = quillWidth - containerWidth;
    }

    // handle vertical positioning
    if (this.options.defaultMenuOrientation === 'top') {
      // Attempt to align the mention container with the top of the quill editor
      if (this.options.fixMentionsToQuill) {
        topPos = -1 * (containerHeight + this.options.offsetTop);
      } else {
        topPos = emojiCharPos.top - (containerHeight + this.options.offsetTop);
      }

      // default to bottom if the top is not visible
      if (topPos + containerPos.top <= 0) {
        let overEmojiCharPos = this.options.offsetTop;

        if (this.options.fixMentionsToQuill) {
          overEmojiCharPos += containerPos.height;
        } else {
          overEmojiCharPos += emojiCharPos.bottom;
        }

        topPos = overEmojiCharPos;
      }
    } else {
      // Attempt to align the mention container with the bottom of the quill editor
      if (this.options.fixMentionsToQuill) {
        topPos += containerPos.height;
      } else {
        topPos += emojiCharPos.bottom;
      }

      // default to the top if the bottom is not visible
      if (this.containerBottomIsNotVisible(topPos, containerPos)) {
        let overEmojiCharPos = this.options.offsetTop * -1;

        if (!this.options.fixMentionsToQuill) {
          overEmojiCharPos += emojiCharPos.top;
        }

        topPos = overEmojiCharPos - containerHeight;
      }
    }

    if (topPos >= 0) {
      this.options.emojiContainerClass.split(' ').forEach((className) => {
        this.emojiContainer.classList.add(`${className}-bottom`);
        this.emojiContainer.classList.remove(`${className}-top`);
      });
    } else {
      this.options.emojiContainerClass.split(' ').forEach((className) => {
        this.emojiContainer.classList.add(`${className}-top`);
        this.emojiContainer.classList.remove(`${className}-bottom`);
      });
    }

    this.emojiContainer.style.top = `${topPos}px`;
    this.emojiContainer.style.left = `${leftPos}px`;
    this.emojiContainer.style.visibility = 'visible';
  }

  setEmojiContainerPosition_Fixed() {
    this.emojiContainer.style.position = 'fixed';
    this.emojiContainer.style.height = null;

    const containerPos = this.quill.container.getBoundingClientRect();
    const emojiCharPos = this.quill.getBounds(this.emojiCharPos);
    const emojiCharPosAbsolute = {
      left: containerPos.left + emojiCharPos.left,
      top: containerPos.top + emojiCharPos.top,
      width: 0,
      height: emojiCharPos.height,
    };

    //Which rectangle should it be relative to
    const relativeToPos = this.options.fixMentionsToQuill
      ? containerPos
      : emojiCharPosAbsolute;

    let topPos = this.options.offsetTop;
    let leftPos = this.options.offsetLeft;

    // handle horizontal positioning
    if (this.options.fixMentionsToQuill) {
      const rightPos = relativeToPos.right;
      this.emojiContainer.style.right = `${rightPos}px`;
    } else {
      leftPos += relativeToPos.left;

      //if its off the righ edge, push it back
      if (
        leftPos + this.emojiContainer.offsetWidth >
        document.documentElement.clientWidth
      ) {
        leftPos -=
          leftPos +
          this.emojiContainer.offsetWidth -
          document.documentElement.clientWidth;
      }
    }

    const availableSpaceTop = relativeToPos.top;
    const availableSpaceBottom =
      document.documentElement.clientHeight -
      (relativeToPos.top + relativeToPos.height);

    const fitsBottom = this.emojiContainer.offsetHeight <= availableSpaceBottom;
    const fitsTop = this.emojiContainer.offsetHeight <= availableSpaceTop;

    var placement;

    if (this.options.defaultMenuOrientation === 'top' && fitsTop) {
      placement = 'top';
    } else if (this.options.defaultMenuOrientation === 'bottom' && fitsBottom) {
      placement = 'bottom';
    } else {
      //it doesnt fit either so put it where there's the most space
      placement = availableSpaceBottom > availableSpaceTop ? 'bottom' : 'top';
    }

    if (placement === 'bottom') {
      topPos = relativeToPos.top + relativeToPos.height;
      if (!fitsBottom) {
        //shrink it to fit
        //3 is a bit of a fudge factor so it doesnt touch the edge of the screen
        this.emojiContainer.style.height = availableSpaceBottom - 3 + 'px';
      }

      this.options.emojiContainerClass.split(' ').forEach((className) => {
        this.emojiContainer.classList.add(`${className}-bottom`);
        this.emojiContainer.classList.remove(`${className}-top`);
      });
    } else {
      topPos = relativeToPos.top - this.emojiContainer.offsetHeight;
      if (!fitsTop) {
        //shrink it to fit
        //3 is a bit of a fudge factor so it doesnt touch the edge of the screen
        this.emojiContainer.style.height = availableSpaceTop - 3 + 'px';
        topPos = 3;
      }

      this.options.emojiContainerClass.split(' ').forEach((className) => {
        this.emojiContainer.classList.add(`${className}-top`);
        this.emojiContainer.classList.remove(`${className}-bottom`);
      });
    }

    this.emojiContainer.style.top = `${topPos}px`;
    this.emojiContainer.style.left = `${leftPos}px`;
    this.emojiContainer.style.visibility = 'visible';
  }

  getTextBeforeCursor() {
    const startPos = Math.max(0, this.cursorPos - this.options.maxChars);
    const textBeforeCursorPos = this.quill.getText(
      startPos,
      this.cursorPos - startPos,
    );
    return textBeforeCursorPos;
  }

  onSomethingChange() {
    const range = this.quill.getSelection();
    if (range == null) return;

    this.cursorPos = range.index;
    const textBeforeCursor = this.getTextBeforeCursor();
    const { mentionChar, mentionCharIndex } = getMentionCharIndex(
      textBeforeCursor,
      this.options.mentionDenotationChars,
    );

    if (
      hasValidMentionCharIndex(
        mentionCharIndex,
        textBeforeCursor,
        this.options.isolateCharacter,
      )
    ) {
      const emojiCharPos =
        this.cursorPos - (textBeforeCursor.length - mentionCharIndex);
      this.emojiCharPos = emojiCharPos;
      const textAfter = textBeforeCursor.substring(
        mentionCharIndex + mentionChar.length,
      );
      if (
        textAfter.length >= this.options.minChars &&
        hasValidChars(textAfter, this.getAllowedCharsRegex(mentionChar))
      ) {
        if (this.existingSourceExecutionToken) {
          this.existingSourceExecutionToken.abandoned = true;
        }
        this.renderLoading();
        var sourceRequestToken = {
          abandoned: false,
        };
        this.existingSourceExecutionToken = sourceRequestToken;
        this.options.source(
          textAfter,
          (data, searchTerm) => {
            if (sourceRequestToken.abandoned) {
              return;
            }
            this.existingSourceExecutionToken = null;

            this.renderList(mentionChar, data, searchTerm);
          },
          mentionChar,
        );
      } else {
        if (this.existingSourceExecutionToken) {
          this.existingSourceExecutionToken.abandoned = true;
        }
        this.hideEmojiList();
      }
    } else {
      if (this.existingSourceExecutionToken) {
        this.existingSourceExecutionToken.abandoned = true;
      }
      this.hideEmojiList();
    }
  }

  getAllowedCharsRegex(denotationChar) {
    if (this.options.allowedChars instanceof RegExp) {
      return this.options.allowedChars;
    } else {
      return this.options.allowedChars(denotationChar);
    }
  }

  onTextChange(delta, oldDelta, source) {
    if (source === 'user') {
      this.onSomethingChange();
    }
  }

  onSelectionChange(range) {
    if (range && range.length === 0) {
      this.onSomethingChange();
    } else {
      this.hideEmojiList();
    }
  }

  openMenu(denotationChar) {
    var selection = this.quill.getSelection(true);
    this.quill.insertText(selection.index, denotationChar);
    this.quill.blur();
    this.quill.focus();
  }
}

export default CustomEmoji;
